import React from 'react';
import {useShowToast} from '@aboutyou/ui-components';
import styled from 'styled-components';
import {useTranslation} from '@aboutyou/i18n';

export enum DiscountNotificationType {
  DiscountStart = 'DiscountStart',
  SwitchExpired = 'SwitchExpired',
  ExpiredWithEmptyDiscounts = 'ExpiredWithEmptyDiscounts',
  Switch = 'Switch',
  DiscountActivated = 'DiscountActivated',
  DiscountSwitched = 'DiscountSwitched',
  DiscountDeActivated = 'DiscountDeActivated',
  DiscountDeactivatedWithFallback = 'DiscountDeactivatedWithFallback',
}

export const useDiscountNotification = () => {
  const showToast = useShowToast();
  const {t} = useTranslation();

  const showNotification = ({type}: {type: DiscountNotificationType}) => {
    switch (type) {
      case DiscountNotificationType.DiscountStart:
        showToast({
          type: 'SUCCESS',
          content: (
            <>
              {t('A new discount is active now!')}
              <Subline>
                {t(
                  'Reductions are already applied to prices shown in the shop.',
                )}
              </Subline>
            </>
          ),
          'data-testid': `DiscountNotification${DiscountNotificationType.DiscountStart}`,
        });
        break;
      case DiscountNotificationType.ExpiredWithEmptyDiscounts:
        showToast({
          type: 'WARNING',
          content: (
            <>
              {t('Your discount was deactivated.')}
              <Subline>{t('The discount expired.')}</Subline>
            </>
          ),
          'data-testid': `DiscountNotification${DiscountNotificationType.ExpiredWithEmptyDiscounts}`,
        });
        break;
      case DiscountNotificationType.Switch:
        showToast({
          type: 'SUCCESS',
          content: (
            <>
              {t('A new discount is active now!')}
              <Subline>{t('We activated a new discount for you.')}</Subline>
            </>
          ),
          'data-testid': `DiscountNotification${DiscountNotificationType.Switch}`,
        });
        break;
      case DiscountNotificationType.SwitchExpired:
        showToast({
          type: 'SUCCESS',
          content: (
            <>
              {t('A new discount is active now!')}
              <Subline>
                {t(
                  'Your previous discount expired. We activated another discount for you.',
                )}
              </Subline>
            </>
          ),
          'data-testid': `DiscountNotification${DiscountNotificationType.SwitchExpired}`,
        });
        break;
      case DiscountNotificationType.DiscountActivated:
        showToast({
          type: 'SUCCESS',
          content: (
            <>
              {t('Your discount is active now!')}
              <Subline>{t('Have fun shopping.')}</Subline>
            </>
          ),
          'data-testid': `DiscountNotification${DiscountNotificationType.DiscountActivated}`,
        });
        break;
      case DiscountNotificationType.DiscountSwitched:
        showToast({
          type: 'SUCCESS',
          content: (
            <>
              {t('Your discount is active now!')}
              <Subline>
                {t(
                  'We deactivated the last discount. You can select one at a time.',
                )}
              </Subline>
            </>
          ),
          'data-testid': `DiscountNotification${DiscountNotificationType.DiscountSwitched}`,
        });
        break;
      case DiscountNotificationType.DiscountDeActivated:
        showToast({
          type: 'WARNING',
          content: (
            <>
              {t('Your discount was deactivated.')}
              <Subline>
                {t(
                  'We updated the prices. You can use your discount by activating it again.',
                )}
              </Subline>
            </>
          ),
          'data-testid': `DiscountNotification${DiscountNotificationType.DiscountDeActivated}`,
        });
        break;
      case DiscountNotificationType.DiscountDeactivatedWithFallback:
        showToast({
          type: 'SUCCESS',
          content: (
            <>
              {t('Your discount was deactivated.')}
              <Subline>
                {t(
                  'We activated another campaign discount for you. You can use your personal discount by activating it again.',
                )}
              </Subline>
            </>
          ),
          'data-testid': `DiscountNotification${DiscountNotificationType.DiscountDeactivatedWithFallback}`,
        });
        break;
      default:
        break;
    }
  };

  return {
    showNotification,
  };
};

const Subline = styled.div`
  font-size: 12px;
`;
